import React, { lazy, Suspense, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "@/components/AuthProvider";
import { RequireAuth } from "@/components/RequireAuth";
import { Toaster } from "@/components/ui/toaster";

import { PWAUpdateNotifier } from "@/components/PWAUpdateNotifier";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { NetworkStatusIndicator } from "@/components/NetworkStatusIndicator";
import { useCapacitorInit } from "@/hooks/use-capacitor-init";
import { useAppLifecycle } from "@/hooks/use-app-lifecycle";
import { SharedElementProvider } from "@/contexts/SharedElementContext";
import { RoxboxPlayerProvider } from "@/contexts/RoxboxPlayerContext";
import { GlobalMiniPlayer } from "@/components/roxbox/GlobalMiniPlayer";
import { AppReadinessProvider } from "@/contexts/AppReadinessContext";
import { AppLoadingGate } from "@/components/AppLoadingGate";
import { AppFlowProvider } from "@/contexts/AppFlowContext";
import { AppFlowOverlay } from "@/components/AppFlowOverlay";

import { MainLayout } from "@/components/MainLayout";

// Critical path - keep eager loaded
import Index from "@/pages/Index";
import Auth from "@/pages/Auth";

// LetsBegin layout should be eager to avoid background flashes between steps
import LetsBeginLayout from "@/pages/letsbegin/LetsBeginLayout";
import LetsBegin from "@/pages/LetsBegin";
import LetsBeginGo from "@/pages/LetsBeginGo";

// UserFlow layout should be eager to avoid background flashes between steps
import UserFlowLayout from "@/pages/userflow/UserFlowLayout";

// Main tabs - eager loaded for instant switching
import MyBundles from "@/pages/MyBundles";
import Search from "@/pages/Search";

// Eager-loaded for instant navigation
import Roxbox from "@/pages/Roxbox";

// Lazy-loaded pages - reduces initial bundle significantly
const ReaderV2 = lazy(() => import("@/pages/ReaderV2"));
const Checkout = lazy(() => import("@/pages/Checkout"));
const Profile = lazy(() => import("@/pages/Profile"));
const Settings = lazy(() => import("@/pages/Settings"));
const ProfileSelection = lazy(() => import("@/pages/ProfileSelection"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));

// Auth flow pages - only needed during authentication
const AuthSignup = lazy(() => import("@/pages/AuthSignup"));
const AuthOTPVerification = lazy(() => import("@/pages/AuthOTPVerification"));
const EmailVerification = lazy(() => import("@/pages/EmailVerification"));

// UserFlow pages - eager loaded to avoid background flashes
import UserFlowEntry from "@/pages/userflow/UserFlowEntry";
import UserFlowGo from "@/pages/userflow/UserFlowGo";

// Onboarding flow
const OnboardingOrchestrator = lazy(() => import("@/pages/onboarding/OnboardingOrchestrator"));

// Utility pages - rarely accessed
const RefundPolicy = lazy(() => import("@/pages/RefundPolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const ShareReceiver = lazy(() => import("@/pages/ShareReceiver"));
const LoadingPage = lazy(() => import("@/components/LoadingPage").then(m => ({ default: m.LoadingPage })));

// Lazy-loaded global component
const GlobalTierSelectorHost = lazy(() => import("@/components/subscription/GlobalTierSelectorHost"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 2 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
    },
  },
});

// Auth-aware Index component - overlay handles profile selection now
function AuthAwareIndex() {
  const { isLoading } = useAuth();
  
  // While auth is loading, show nothing to prevent flash
  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-background" />
    );
  }
  
  // AppFlowOverlay will show profile selection if needed
  return <Index />;
}

function AppContent() {
  useCapacitorInit();
  useAppLifecycle();
  const location = useLocation();

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      const handleMessage = async (event: MessageEvent) => {
        // Reserved for future sync functionality
      };

      navigator.serviceWorker.addEventListener('message', handleMessage);
      return () => {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
      };
    }
  }, []);

  useEffect(() => {
    const preventContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      return false;
    };

    const preventDragStart = (e: DragEvent) => {
      e.preventDefault();
      return false;
    };

    const preventCopy = (e: ClipboardEvent) => {
      e.preventDefault();
      return false;
    };

    document.addEventListener('contextmenu', preventContextMenu);
    document.addEventListener('dragstart', preventDragStart);
    document.addEventListener('copy', preventCopy);
    document.addEventListener('cut', preventCopy);

    return () => {
      document.removeEventListener('contextmenu', preventContextMenu);
      document.removeEventListener('dragstart', preventDragStart);
      document.removeEventListener('copy', preventCopy);
      document.removeEventListener('cut', preventCopy);
    };
  }, []);


  return (
    <>
      
      <PWAUpdateNotifier />
      <NetworkStatusIndicator />
      <div id="main-content">
        <Suspense fallback={null}>
          <Routes>
            {/* Auth routes - no persistent navigation */}
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/signup" element={<AuthSignup />} />
            <Route path="/auth/verify-otp" element={<AuthOTPVerification />} />
            
            {/* LetsBegin flow with persistent ShaderGradient background */}
            <Route path="/letsbegin" element={<LetsBeginLayout />}>
              <Route index element={<LetsBegin />} />
              <Route path="go" element={<LetsBeginGo />} />
            </Route>

            {/* Userflow steps with persistent background */}
            <Route path="/userflow" element={<UserFlowLayout />}>
              <Route index element={<UserFlowEntry />} />
              <Route path="go" element={<UserFlowGo />} />
            </Route>

            <Route path="/verify-email" element={<EmailVerification />} />

            {/* Onboarding flow - no persistent navigation */}
            <Route
              path="/onboarding/*"
              element={
                <RequireAuth requireProfileSelection={false}>
                  <OnboardingOrchestrator />
                </RequireAuth>
              }
            />

            {/* Loading page */}
            <Route
              path="/loading"
              element={
                <RequireAuth>
                  <LoadingPage />
                </RequireAuth>
              }
            />

            {/* Backwards compatibility redirects for /launch and /profiles */}
            <Route path="/launch" element={<Navigate to="/" replace />} />
            <Route path="/profiles" element={<Navigate to="/" replace />} />

            {/* Reader - no persistent navigation */}
            <Route
              path="/read/:bookId"
              element={
                <RequireAuth requireProfileSelection={true}>
                  <ReaderV2 />
                </RequireAuth>
              }
            />

            {/* Routes with persistent navigation */}
            <Route element={<MainLayout />}>
              <Route path="/" element={<AuthAwareIndex />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/search" element={<Search />} />
              <Route path="/jukebox" element={<Roxbox />} />
              {/* Redirect old roxbox URL to jukebox */}
              <Route path="/roxbox" element={<Navigate to="/jukebox" replace />} />

              <Route
                path="/my-bundles"
                element={
                  <RequireAuth requireProfileSelection={true}>
                    <MyBundles />
                  </RequireAuth>
                }
              />
              <Route
                path="/admin"
                element={
                  <RequireAuth requireProfileSelection={true}>
                    <AdminDashboard />
                  </RequireAuth>
                }
              />
              <Route
                path="/profile"
                element={
                  <RequireAuth requireProfileSelection={true}>
                    <Profile />
                  </RequireAuth>
                }
              />
              <Route
                path="/settings"
                element={
                  <RequireAuth requireProfileSelection={true}>
                    <Settings />
                  </RequireAuth>
                }
              />
              <Route
                path="/settings/offline-storage"
                element={
                  <RequireAuth requireProfileSelection={true}>
                    <div>Offline Storage - Coming Soon</div>
                  </RequireAuth>
                }
              />
            </Route>

            {/* Utility routes - no persistent navigation */}
            <Route path="/share" element={<ShareReceiver />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
          </Routes>
        </Suspense>
      </div>
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Router>
          <AuthProvider>
            <AppReadinessProvider>
              <AppFlowProvider>
                <SharedElementProvider>
                  <RoxboxPlayerProvider>
                    <AppLoadingGate>
                      <AppContent />
                    </AppLoadingGate>
                    {/* Profile/Launch overlay - renders above content when needed */}
                    <AppFlowOverlay />
                    <GlobalMiniPlayer />
                    <Suspense fallback={null}>
                      <GlobalTierSelectorHost />
                    </Suspense>
                    <Toaster />
                  </RoxboxPlayerProvider>
                </SharedElementProvider>
              </AppFlowProvider>
            </AppReadinessProvider>
          </AuthProvider>
        </Router>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
